import { FC, useEffect } from 'react';
import { useSelector } from 'react-redux';
import CarouselButtons from '../presentational/CarouselButtons';
import { useFreezeCardMutation, useDefrostCardMutation } from 'lib/services';
import { selectCardIsPosting } from 'lib/slices/cardsSlice';
import { useRestrictedAction } from 'hooks';
import { CardStatus, ICard } from 'lib/type';
import useCountdownTimer from 'lib/hooks/useCountdownTimer';

interface CarouselButtonsContainerProps {
    onPrevClick: () => void;
    onNextClick: () => void;
    onViewPinClick: () => void;
    start: boolean;
    end: boolean;
    card: ICard | null;
    isDormant: boolean;
}

const CarouselButtonsContainer: FC<CarouselButtonsContainerProps> = ({
    onViewPinClick,
    card,
    isDormant,
}) => {
    const [freezeCard] = useFreezeCardMutation();
    const [defrostCard] = useDefrostCardMutation();
    const isPosting = useSelector(selectCardIsPosting);
    const status = card?.status;
    const isFrozen = status === CardStatus.TERMINATED;

    const storedEndTime = sessionStorage.getItem('countdownEndTime');

    const initialValue = storedEndTime
        ? Math.max(
              0,
              Math.floor(
                  (Number.parseInt(storedEndTime, 10) - Date.now()) / 1000,
              ),
          )
        : 30;

    const {
        remainingSeconds,
        startCountdown,
        isCountdownActive,
    } = useCountdownTimer(initialValue);

    const isDisabledCard =
        isDormant ||
        !status ||
        ([
            CardStatus.LOST,
            CardStatus.STOLEN,
            CardStatus.DAMAGED,
            CardStatus.ISSUED,
            CardStatus.RISK,
            'N',
        ] as (CardStatus | string)[]).includes(status);

    const { handleRestrictedAction } = useRestrictedAction();

    const toggleFreeze = async () => {
        if (!card) return;

        if (isFrozen) {
            await defrostCard(card.cardSerial);
        } else {
            await freezeCard(card.cardSerial);
        }

        const endTime = Date.now() + 30 * 1000;
        sessionStorage.setItem('countdownEndTime', endTime.toString());
        startCountdown();
    };

    useEffect(() => {
        if (storedEndTime) {
            const secondsLeft = Math.max(
                0,
                Math.floor(
                    (Number.parseInt(storedEndTime, 10) - Date.now()) / 1000,
                ),
            );
            if (secondsLeft > 0) {
                startCountdown();
            }
        }
    }, [storedEndTime, startCountdown]);

    return (
        <CarouselButtons
            onViewPinClick={onViewPinClick}
            toggleFreeze={() => handleRestrictedAction(toggleFreeze)}
            isFrozen={isFrozen}
            isDisabledCard={isDisabledCard}
            remainingSeconds={remainingSeconds}
            isCountdownActive={isCountdownActive}
            isPosting={isPosting}
        />
    );
};

export default CarouselButtonsContainer;
